import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from '@/hooks/use-toast';
import { ClipboardList, Plus, Trash2, ArrowUp, ArrowDown } from 'lucide-react';
import { useShiftReportTemplate, useUpdateShiftReportTemplate } from '@/hooks/useShiftReportTemplate';
import {
  DEFAULT_SHIFT_REPORT_TEMPLATE,
  createShiftReportField,
  normalizeShiftReportTemplate,
  validateShiftReportTemplate,
  type ShiftReportField,
  type ShiftReportTemplate,
} from '@/utils/shiftReportTemplate';

const normalizeOrders = (template: ShiftReportTemplate): ShiftReportTemplate => template.map((section, sectionIndex) => ({
  ...section,
  order: sectionIndex + 1,
  fields: section.fields.map((field, fieldIndex) => ({ ...field, order: fieldIndex + 1 })),
}));

export const ShiftReportFieldsSettings = () => {
  const { data: savedTemplate = DEFAULT_SHIFT_REPORT_TEMPLATE, isLoading } = useShiftReportTemplate();
  const updateMut = useUpdateShiftReportTemplate();
  const [template, setTemplate] = useState<ShiftReportTemplate>(DEFAULT_SHIFT_REPORT_TEMPLATE);

  useEffect(() => {
    setTemplate(normalizeShiftReportTemplate(savedTemplate));
  }, [savedTemplate]);

  const updateField = (sectionId: string, fieldId: string, patch: Partial<ShiftReportField>) => {
    setTemplate((prev) => prev.map((section) => section.id === sectionId
      ? { ...section, fields: section.fields.map((field) => field.id === fieldId ? { ...field, ...patch } : field) }
      : section));
  };

  const moveField = (sectionId: string, fieldId: string, direction: -1 | 1) => {
    setTemplate((prev) => normalizeOrders(prev.map((section) => {
      if (section.id !== sectionId) return section;
      const fields = [...section.fields];
      const index = fields.findIndex((field) => field.id === fieldId);
      const nextIndex = index + direction;
      if (index < 0 || nextIndex < 0 || nextIndex >= fields.length) return section;
      [fields[index], fields[nextIndex]] = [fields[nextIndex], fields[index]];
      return { ...section, fields };
    })));
  };

  const moveFieldToSection = (fromSectionId: string, fieldId: string, toSectionId: string) => {
    if (fromSectionId === toSectionId) return;
    setTemplate((prev) => {
      const source = prev.find((section) => section.id === fromSectionId);
      const field = source?.fields.find((item) => item.id === fieldId);
      if (!field) return prev;
      return normalizeOrders(prev.map((section) => {
        if (section.id === fromSectionId) return { ...section, fields: section.fields.filter((item) => item.id !== fieldId) };
        if (section.id === toSectionId) return { ...section, fields: [...section.fields, field] };
        return section;
      }));
    });
  };

  const addField = (sectionId: string) => {
    setTemplate((prev) => prev.map((section) => section.id === sectionId
      ? { ...section, fields: [...section.fields, createShiftReportField(sectionId, section.fields.length)] }
      : section));
  };

  const removeField = (sectionId: string, fieldId: string) => {
    setTemplate((prev) => prev.map((section) => section.id === sectionId
      ? { ...section, fields: section.fields.filter((field) => field.id !== fieldId) }
      : section));
  };

  const toggleSection = (sectionId: string, enabled: boolean) => {
    setTemplate((prev) => prev.map((section) => section.id === sectionId ? { ...section, enabled } : section));
  };

  const removeSection = (sectionId: string) => {
    if (!confirm('Remove this entire section? Saved shift reports keep their data, but new reports will not include these fields.')) return;
    setTemplate((prev) => normalizeOrders(prev.filter((section) => section.id !== sectionId)));
  };

  const addSection = () => {
    const id = `section-${Date.now()}`;
    setTemplate((prev) => normalizeOrders([
      ...prev,
      { id, title: 'New Section', order: prev.length + 1, enabled: true, fields: [createShiftReportField(id, 0)] },
    ]));
  };

  const save = () => {
    const normalized = normalizeOrders(template.map((section) => ({
      ...section,
      title: section.title.trim(),
      fields: section.fields.map((field) => ({ ...field, label: field.label.trim(), placeholder: field.placeholder?.trim() || '' })),
    })));
    const error = validateShiftReportTemplate(normalized);
    if (error) {
      toast({ variant: 'destructive', title: 'Unable to save fields', description: error });
      return;
    }
    updateMut.mutate(normalized, {
      onSuccess: () => toast({ title: 'Shift Report Fields Saved', description: 'Shift Details, print, and PDF exports will use these fields.' }),
      onError: (e: any) => toast({ variant: 'destructive', title: 'Unable to save fields', description: e.message || 'Please wait a moment and try again.' }),
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ClipboardList className="h-5 w-5" /> Shift Report Fields
        </CardTitle>
        <CardDescription>Choose which fields appear in Shift Details, print reports, and PDF exports.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {template.map((section) => {
          const sectionEnabled = section.enabled !== false;
          return (
          <div key={section.id} className={`rounded-md border p-3 space-y-3 ${sectionEnabled ? '' : 'opacity-60'}`}>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
              <div className="space-y-1.5 sm:max-w-sm flex-1">
                <Label>Section Name</Label>
                <Input
                  value={section.title}
                  onChange={(e) => setTemplate((prev) => prev.map((item) => item.id === section.id ? { ...item, title: e.target.value } : item))}
                />
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <div className="flex items-center gap-2 h-10 px-2 rounded-md border">
                  <Switch checked={sectionEnabled} onCheckedChange={(v) => toggleSection(section.id, v)} aria-label="Toggle section" />
                  <Badge variant={sectionEnabled ? 'default' : 'secondary'}>{sectionEnabled ? 'Section On' : 'Section Off'}</Badge>
                </div>
                <Button type="button" variant="outline" size="sm" onClick={() => addField(section.id)}>
                  <Plus className="h-4 w-4 mr-2" /> Add Field
                </Button>
                <Button type="button" variant="outline" size="icon" onClick={() => removeSection(section.id)} aria-label="Remove section">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              {section.fields.map((field, index) => (
                <div key={field.id} className="grid grid-cols-1 lg:grid-cols-[minmax(180px,1.4fr)_150px_150px_auto_auto_auto_auto] gap-2 items-end rounded-md bg-muted/30 p-2">
                  <div className="space-y-1.5">
                    <Label>Field Label</Label>
                    <Input value={field.label} onChange={(e) => updateField(section.id, field.id, { label: e.target.value })} />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Input Type</Label>
                    <Select
                      value={field.inputType || 'text'}
                      onValueChange={(value) => updateField(section.id, field.id, { inputType: value as any })}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="text">Text</SelectItem>
                        <SelectItem value="staff">Staff (single)</SelectItem>
                        <SelectItem value="staffMulti">Staff (multi)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Section</Label>
                    <Select value={section.id} onValueChange={(value) => moveFieldToSection(section.id, field.id, value)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {template.map((option) => (
                          <SelectItem key={option.id} value={option.id}>{option.title}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2 h-10">
                    <Switch
                      checked={field.enabled}
                      onCheckedChange={(enabled) => updateField(section.id, field.id, { enabled })}
                      disabled={field.required}
                    />
                    <Badge variant={field.required ? 'default' : (field.enabled ? 'default' : 'secondary')}>
                      {field.required ? 'Required' : (field.enabled ? 'On' : 'Off')}
                    </Badge>
                  </div>
                  <Button type="button" variant="outline" size="icon" onClick={() => moveField(section.id, field.id, -1)} disabled={index === 0} aria-label="Move field up">
                    <ArrowUp className="h-4 w-4" />
                  </Button>
                  <Button type="button" variant="outline" size="icon" onClick={() => moveField(section.id, field.id, 1)} disabled={index === section.fields.length - 1} aria-label="Move field down">
                    <ArrowDown className="h-4 w-4" />
                  </Button>
                  <Button type="button" variant="outline" size="icon" onClick={() => removeField(section.id, field.id)} aria-label={`Remove ${field.label}`} disabled={field.required}>
                    <Trash2 className="h-4 w-4" />
                  </Button>

                </div>
              ))}
              {section.fields.length === 0 && (
                <p className="text-sm text-muted-foreground py-2">No fields in this section.</p>
              )}
            </div>
          </div>
          );
        })}

        <Button type="button" variant="outline" onClick={addSection}>
          <Plus className="h-4 w-4 mr-2" /> Add Section
        </Button>
        <div className="flex flex-col sm:flex-row gap-2 sm:justify-between">
          <Button type="button" variant="outline" onClick={() => setTemplate(normalizeShiftReportTemplate(DEFAULT_SHIFT_REPORT_TEMPLATE))}>
            Reset to Default Fields
          </Button>
          <Button onClick={save} disabled={isLoading || updateMut.isPending}>
            {updateMut.isPending ? 'Saving…' : 'Save Shift Report Fields'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
